/**
 * The workdoc block model (`specs/M11-docs.md` §2).
 *
 * monday stores a block's body as a JSON **string** holding a Quill delta plus a handful of
 * per-type keys. The importer keeps it verbatim (`specs/M12-X2-importer.md` §20) and nothing
 * downstream rewrites it, so this module only ever *reads* it: it maps the raw object onto a
 * small typed shape for the renderer and maps back for the two things we write (a text edit
 * and a checklist tick).
 *
 * Every type name, key and attribute below was measured, not guessed — the 1,603 blocks of the
 * account's 20 docs (`discovery/ui-reference/docs/INDEX.md` §1) and a 16-value probe of
 * `create_doc_block` against the sandbox (`discovery/api-corpus/export-docs/probe-docs.json`).
 */
/* ── types ──────────────────────────────────────────────────────────────── */
/**
 * The block types monday **reads back**, space-separated. The write enum
 * (`DocBlockContentType`) is the same list with underscores; see `docBlockTypeToApi`.
 *
 * The first thirteen are the ones present in the account's docs, in frequency order; `quote`
 * and `code` are accepted by `create_doc_block` but used by no imported doc.
 */
export const DOC_BLOCK_TYPES = [
    'normal text',
    'check list',
    'bulleted list',
    'medium title',
    'cell',
    'large title',
    'numbered list',
    'divider',
    'small title',
    'image',
    'board',
    'file',
    'table',
    'quote',
    'code',
];
/** The thirteen that actually occur in the 20 imported docs (M11-1 asserts this set). */
export const DOC_BLOCK_TYPES_IN_USE = [
    'normal text',
    'check list',
    'bulleted list',
    'medium title',
    'cell',
    'large title',
    'numbered list',
    'divider',
    'small title',
    'image',
    'board',
    'file',
    'table',
];
const DOC_BLOCK_TYPE_SET = new Set(DOC_BLOCK_TYPES);
export function isDocBlockType(value) {
    return DOC_BLOCK_TYPE_SET.has(value);
}
/**
 * `create_doc_block(type: DocBlockContentType!)` takes `normal_text` and the block reads back
 * as `normal text` — measured on the sandbox for all nine types monday accepts.
 */
export function docBlockTypeToApi(type) {
    return type.replace(/ /g, '_');
}
/** The inverse. An unknown value falls back to `normal text`, never to an error. */
export function docBlockTypeFromApi(value) {
    const spaced = String(value ?? '').replace(/_/g, ' ').trim().toLowerCase();
    return isDocBlockType(spaced) ? spaced : 'normal text';
}
/**
 * Seven of the sixteen `DocBlockContentType` values are refused by monday itself with a plain
 * `bad request` — including `divider`, `image` and `table`, which its own UI creates and which
 * the account's docs contain. Ours accepts three of them (deviation D-M11-1, §2.2), because an
 * editor that cannot reproduce its own documents is worse than a divergence.
 */
export const MONDAY_REJECTS_CREATE = [
    'divider',
    'notice_box',
    'page_break',
    'image',
    'video',
    'table',
    'layout',
];
/** The types this product can create through the `/` menu. */
export const CREATABLE_DOC_BLOCK_TYPES = [
    'normal text',
    'large title',
    'medium title',
    'small title',
    'quote',
    'code',
    'bulleted list',
    'numbered list',
    'check list',
    'divider',
];
function asOps(content) {
    const ops = content?.deltaFormat;
    return Array.isArray(ops) ? ops : [];
}
/** The plain text of a block — what search, the markdown export and a title need. */
export function deltaToPlainText(content) {
    let out = '';
    for (const op of asOps(content)) {
        if (typeof op.insert === 'string')
            out += op.insert;
    }
    return out;
}
/**
 * The runs a renderer draws. An embed insert becomes nothing rather than `[object Object]`,
 * and an empty delta yields an empty array (the caller draws a placeholder line).
 */
export function deltaToRuns(content) {
    const runs = [];
    for (const op of asOps(content)) {
        if (typeof op.insert !== 'string' || op.insert === '')
            continue;
        const attributes = op.attributes ?? {};
        runs.push({
            text: op.insert,
            bold: attributes.bold === true,
            italic: attributes.italic === true,
            underline: attributes.underline === true,
            strike: attributes.strike === true,
            link: typeof attributes.link === 'string' ? attributes.link : null,
            color: typeof attributes.color === 'string' ? attributes.color : null,
            background: typeof attributes.background === 'string' ? attributes.background : null,
            fontSize: attributes.sanitizedSize === undefined || attributes.sanitizedSize === null
                ? null
                : String(attributes.sanitizedSize),
        });
    }
    return runs;
}
/**
 * A plain-text edit, written back into the shape monday stores. Everything except
 * `deltaFormat` is preserved, so an edit never drops alignment, indentation or a tick.
 *
 * Empty text keeps an **empty** `deltaFormat`, which is what monday stores for a blank line —
 * not a missing key, which would read as "never had a body".
 */
export function withPlainText(content, text) {
    const base = { ...(content ?? {}) };
    base.deltaFormat = text === '' ? [] : [{ insert: text }];
    return base;
}
/** A checklist tick. `checked` is the only key monday moves for it. */
export function withChecked(content, checked) {
    return { ...(content ?? {}), checked };
}
/** monday ships `content` as a JSON string; an unparseable one is kept as `{ raw }`. */
export function parseDocBlockContent(raw) {
    if (raw === null || raw === undefined)
        return {};
    if (typeof raw === 'object' && !Array.isArray(raw))
        return raw;
    if (typeof raw !== 'string' || raw.trim() === '')
        return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed
            : { raw };
    }
    catch {
        return { raw };
    }
}
/**
 * Assemble the flat rows into the tree the renderer walks.
 *
 * 70 of the account's 1,603 blocks are nested (a `table`'s `cell`s, and the `normal text`
 * inside each cell), which closes q407 — the importer's note that "all 1,603 are top level" is
 * wrong. A `table`'s *layout* still comes from `content.cells`, not from these children:
 * the importer re-mints positions across the whole doc, so a cell's position says nothing
 * about its column.
 *
 * A block whose parent is missing is promoted to the top level rather than dropped.
 */
export function buildDocBlockTree(rows) {
    const byId = new Map();
    for (const row of rows) {
        byId.set(row.id, { ...row, type: docBlockTypeFromApi(row.type), children: [] });
    }
    const roots = [];
    for (const row of rows) {
        const node = byId.get(row.id);
        const parent = row.parentBlockId === null ? undefined : byId.get(row.parentBlockId);
        if (parent && parent !== node)
            parent.children.push(node);
        else
            roots.push(node);
    }
    const byPosition = (a, b) => a.position < b.position ? -1 : a.position > b.position ? 1 : a.id < b.id ? -1 : 1;
    roots.sort(byPosition);
    for (const node of byId.values())
        node.children.sort(byPosition);
    return roots;
}
/** A table's grid, resolved to nodes. Missing ids become `null` cells rather than throwing. */
export function tableGrid(table, index) {
    const cells = Array.isArray(table.content.cells) ? table.content.cells : [];
    const lookup = index ?? new Map(table.children.map((child) => [child.id, child]));
    return cells.map((row) => (Array.isArray(row) ? row : []).map((cell) => lookup.get(cell?.blockId ?? '') ?? null));
}
/* ── markdown ───────────────────────────────────────────────────────────── */
/**
 * monday's `export_markdown_from_doc`, reproduced from the sandbox probe. The exact spacing is
 * monday's, oddities included: a plain paragraph is prefixed with **one space**, and a list
 * marker is followed by **two**.
 */
export function docBlocksToMarkdown(nodes) {
    const parts = [];
    for (const node of nodes) {
        const text = deltaToPlainText(node.content);
        switch (node.type) {
            case 'large title':
                parts.push(`# ${text}`);
                break;
            case 'medium title':
                parts.push(`## ${text}`);
                break;
            case 'small title':
                parts.push(`### ${text}`);
                break;
            case 'bulleted list':
                parts.push(`-  ${text}`);
                break;
            case 'numbered list':
                parts.push(`1.  ${text}`);
                break;
            case 'check list':
                parts.push(`- [${node.content.checked === true ? 'x' : ' '}]  ${text}`);
                break;
            case 'quote':
                parts.push(`> ${text}`);
                break;
            case 'code':
                parts.push(['```', text, '```'].join('\n'));
                break;
            case 'divider':
                parts.push('---');
                break;
            case 'image':
                parts.push(`![](${String(node.content.publicUrl ?? node.content.url ?? '')})`);
                break;
            case 'table': {
                const grid = tableGrid(node);
                for (const [rowIndex, row] of grid.entries()) {
                    const cells = row.map((cell) => cell === null ? '' : cell.children.map((child) => deltaToPlainText(child.content)).join(' '));
                    parts.push(`| ${cells.join(' | ')} |`);
                    if (rowIndex === 0)
                        parts.push(`| ${cells.map(() => '-').join(' | ')} |`);
                }
                break;
            }
            case 'cell':
                // Rendered by its table; on its own it contributes nothing.
                break;
            default:
                parts.push(` ${text}`);
        }
    }
    return parts.join('\n\n');
}
//# sourceMappingURL=index.js.map